import React, { useEffect, useState } from 'react';
import { Chip, Grid, MenuItem } from '@mui/material';
import { DndContext, DragEndEvent, DragOverlay, KeyboardSensor, PointerSensor, closestCenter, defaultDropAnimation, useSensor, useSensors } from '@dnd-kit/core';
import { sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import Fieldset from '@/components/_common/Fieldset';
import DropdownSelect from '@/components/_common/Form/DropdownSelect';
import EmailsField from '@/components/_common/EmailsField';
import axios from '@/_utils/axios';
import { useToast } from '@/components/_common/Toast';
import ProfileBuilderPreviewTemplate from './ProfileBuilderPreviewTemplate';

type FieldValueType = string | string[] | Record<string, string | string[] | TrustedHTML>;

type EmailNotification = {
  template: string | TrustedHTML;
  to?: string[];
  cc?: string[];
  bcc?: string[];
};

type ProfileBuilderNotificationsProps = {
  updateField: (field: string, value: FieldValueType) => void;
  email_notification: Partial<EmailNotification>;
  errors: any;
};

const ProfileBuilderNotifications: React.FC<ProfileBuilderNotificationsProps> = ({ updateField, email_notification = {}, errors = {} }) => {
  const { error } = useToast();
  const [templates, setTemplates] = useState<string[]>([]);
  const [activeDrag, setActiveDrag] = useState<string | null>(null);

  useEffect(() => {
    getEmailTemplate();
  }, [])

  const getEmailTemplate = async () => {
    try {
      const res = await axios.post('/core/email_templates/');
      setTemplates(res.data?.data);
    } catch (err) {
      error('Failed to fetch email templates');
    }
  };

  const onChange = (field: keyof EmailNotification, value: FieldValueType) => {
    updateField('email_notification', { ...email_notification, [field]: value })
  }

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const [destinationName] = (over.id as string).split('-');
      const [_group, ...emailParts] = (active.id as string).split('-');
      const value = emailParts.join('');
      const sourceName = active?.data?.current?.sortable?.containerId;
      const destinationExistingValue: string[] = (email_notification[destinationName as keyof EmailNotification] ? email_notification[destinationName as keyof EmailNotification] : []) as string[];
      const sourceExistingValue: string[] = (email_notification[sourceName as keyof EmailNotification] ? email_notification[sourceName as keyof EmailNotification] : []) as string[];

      if (!destinationExistingValue?.includes(value)) {
        const newValue = [...destinationExistingValue, value];
        const sourceNewValue = sourceExistingValue?.filter((val: string) => val !== value);

        updateField('email_notification', {
          ...email_notification,
          [destinationName]: newValue,
          [sourceName]: sourceNewValue,
        });
      }
    }
  };
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={onDragEnd}
      onDragStart={({ active }) => setActiveDrag(active.id as string)}
    >
      <Fieldset label="Delivery Notification" style={{ paddingTop: 40 }}>
        <Grid container spacing={5}>
          <Grid sm={3} item>
            <DropdownSelect fullWidth
              label="Email Templates"
              value={templates.length && email_notification.template || ''}
              onChange={(template: string) => onChange('template', template)}
            >
              <MenuItem key="none" value={''}>None</MenuItem>
              {templates.map(template => <MenuItem key={template} value={template}>{template}</MenuItem>)}
            </DropdownSelect>

            <ProfileBuilderPreviewTemplate 
              disabled={!email_notification?.template} 
              template={email_notification.template as string}
            />
          </Grid>


          <Grid sm={3} item>
            <EmailsField
              name="to"
              disabled={!email_notification?.template}
              label="To:"
              emails={email_notification?.to}
              hasError={errors['email_notification.to']}
              onChange={emails => onChange('to', emails)}
            />
          </Grid>

          <Grid sm={3} item>
            <EmailsField
              name="cc"
              disabled={!email_notification?.template}
              label="CC:"
              emails={email_notification?.cc}
              onChange={emails => onChange('cc', emails)}
            />
          </Grid>

          <Grid sm={3} item>
            <EmailsField
              name="bcc"
              disabled={!email_notification?.template}
              label="BCC:"
              emails={email_notification?.bcc}
              onChange={emails => onChange('bcc', emails)}
            />
          </Grid>
        </Grid>
      </Fieldset>
        <DragOverlay dropAnimation={defaultDropAnimation}>
          {activeDrag ? (
            <Chip style={{ cursor: 'grab' }} variant="filled" label={activeDrag}  />
          ) : null}
        </DragOverlay>
    </DndContext>
  )
}

export default ProfileBuilderNotifications;
